import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ticketsAPI, ticketStatusLabel, Ticket, TicketMessage } from '../../utils/ticketsApi';
import '../../pages/Area/Area.css';
import './Tickets.css';

const dateLabel = (value: string) => {
  const date = new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const mergeMessages = (old: TicketMessage[], fresh: TicketMessage[]) => Array.from(new Map([...old, ...fresh].map(message => [message.id, message])).values()).sort((a, b) => a.id - b.id);

const Tickets: React.FC = () => {
  const { isAdmin } = useAuth();
  const { id } = useParams<{ id: string }>();
  const selectedId = id ? Number(id) : null;
  const navigate = useNavigate();
  const [scope, setScope] = useState<'open' | 'all'>(isAdmin ? 'open' : 'all');
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [messages, setMessages] = useState<TicketMessage[]>([]);
  const [older, setOlder] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [createError, setCreateError] = useState('');
  const listVersion = useRef(0);
  const detailVersion = useRef(0);
  const listPending = useRef(false);
  const detailPending = useRef(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const loadList = useCallback(async (quiet = false) => {
    if (quiet && listPending.current) return;
    const version = ++listVersion.current;
    listPending.current = true;
    if (!quiet) setLoading(true);
    try {
      const response = await ticketsAPI.list(scope, page);
      if (version === listVersion.current) { setTickets(response.tickets); setHasMore(response.has_more); }
    } catch (e) { if (version === listVersion.current) setError((e as Error).message); }
    finally { if (version === listVersion.current) { listPending.current = false; setLoading(false); } }
  }, [scope, page]);

  const loadDetail = useCallback(async (quiet = false) => {
    if (!selectedId || (quiet && detailPending.current)) return;
    const version = ++detailVersion.current;
    detailPending.current = true;
    if (!quiet) setDetailLoading(true);
    try {
      const response = await ticketsAPI.show(selectedId);
      if (version !== detailVersion.current) return;
      setTicket(response.ticket);
      setMessages(previous => quiet ? mergeMessages(previous, response.messages) : response.messages);
      if (!quiet) setOlder(response.has_more);
    } catch (e) { if (version === detailVersion.current) setError((e as Error).message); }
    finally { if (version === detailVersion.current) { detailPending.current = false; setDetailLoading(false); } }
  }, [selectedId]);

  useEffect(() => { void loadList(); return () => { listVersion.current++; listPending.current = false; }; }, [loadList]);
  useEffect(() => {
    setTicket(null); setMessages([]); setDraft(''); setError(''); setOlder(false);
    void loadDetail();
    return () => { detailVersion.current++; detailPending.current = false; };
  }, [loadDetail]);
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible' && !busy) { void loadList(true); void loadDetail(true); }
    }, 15000);
    return () => window.clearInterval(timer);
  }, [loadList, loadDetail, busy]);

  useEffect(() => {
    if (!createOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    const keydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) setCreateOpen(false);
      if (event.key !== 'Tab') return;
      const items = dialogRef.current?.querySelectorAll<HTMLElement>('input, textarea, button:not(:disabled)');
      if (!items?.length) return;
      const first = items[0], last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', keydown);
    return () => { document.body.style.overflow = overflow; document.removeEventListener('keydown', keydown); previous?.focus(); };
  }, [createOpen, busy]);

  const create = async (event: FormEvent) => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setCreateError('');
    try {
      const response = await ticketsAPI.create(subject.trim(), body.trim());
      setCreateOpen(false); setSubject(''); setBody(''); setPage(1); setScope('all');
      navigate(`/tickets/${response.ticket.id}`);
      await loadList(true);
    } catch (e) { setCreateError((e as Error).message); }
    finally { setBusy(false); }
  };
  const send = async (event: FormEvent) => {
    event.preventDefault(); if (!selectedId || busy || !draft.trim()) return;
    setBusy(true); setError('');
    try { await ticketsAPI.reply(selectedId, draft.trim()); setDraft(''); await loadDetail(true); await loadList(true); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };
  const changeStatus = async () => {
    if (!ticket || busy) return;
    setBusy(true); setError('');
    try { await ticketsAPI.status(ticket.id, ticket.status === 'closed' ? 'waiting_support' : 'closed'); await loadDetail(true); await loadList(true); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };
  const loadOlder = async () => {
    if (!selectedId || busy || !messages.length) return;
    const version = detailVersion.current;
    setBusy(true);
    try {
      const response = await ticketsAPI.show(selectedId, messages[0].id);
      if (version === detailVersion.current) { setMessages(previous => mergeMessages(previous, response.messages)); setOlder(response.has_more); }
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return <main className="tickets-page"><div className="container ui-page">
    <header className="ui-page-head"><div className="ui-page-head__text"><h1 className="ui-page-head__title">Поддержка</h1>
      <p className="ui-page-head__subtitle">{isAdmin ? 'Обращения пользователей и история переписки' : 'Задайте вопрос — вся переписка с поддержкой останется здесь'}</p></div>
      {!isAdmin && <button className="ui-btn ui-btn--primary" onClick={() => { setCreateError(''); setCreateOpen(true); }}>+ Создать тикет</button>}
    </header>
    {error && <div className="ui-alert ui-alert--danger" role="alert">{error}<button className="ui-btn ui-btn--outline ui-btn--sm" onClick={() => { setError(''); void loadList(); void loadDetail(); }}>Повторить</button></div>}
    <div className="tickets-tabs" aria-label="Разделы обращений">
      <button className={scope === 'open' ? 'is-active' : ''} aria-pressed={scope === 'open'} onClick={() => { setScope('open'); setPage(1); }}>Открытые тикеты</button>
      <button className={scope === 'all' ? 'is-active' : ''} aria-pressed={scope === 'all'} onClick={() => { setScope('all'); setPage(1); }}>Все запросы</button>
    </div>
    <div className="tickets-layout">
      <section className="ui-card tickets-list" aria-label="Список обращений">
        <div className="tickets-list__heading">Обращения <span>Стр. {page}</span></div>
        {loading ? <div className="ui-loading">Загрузка обращений…</div> : tickets.length === 0 ? <div className="ui-empty"><strong>{scope === 'open' ? 'Открытых тикетов нет' : 'Обращений пока нет'}</strong><span>{isAdmin ? 'Новые обращения появятся здесь' : 'Создайте тикет, если вам нужна помощь'}</span></div> : <ul>
          {tickets.map(item => <li key={item.id}><button disabled={busy} className={`tickets-list__item ${selectedId === item.id ? 'is-selected' : ''}`} onClick={() => navigate(`/tickets/${item.id}`)}>
            <span className="tickets-list__meta">#{item.id}<time>{dateLabel(item.updated_at)}</time></span>
            <strong>{item.subject}</strong>
            {isAdmin && <span className="tickets-list__owner">{item.owner ? `${item.owner.name} · ${item.owner.email}` : 'Удалённый пользователь'}</span>}
            <span className={`ticket-status ticket-status--${item.status}`}>{ticketStatusLabel[item.status]}</span>
          </button></li>)}
        </ul>}
        {(page > 1 || hasMore) && <footer className="tickets-list__pages"><button className="ui-btn ui-btn--outline ui-btn--sm" disabled={loading || page === 1} onClick={() => setPage(value => value - 1)}>Назад</button><button className="ui-btn ui-btn--outline ui-btn--sm" disabled={loading || !hasMore} onClick={() => setPage(value => value + 1)}>Вперёд</button></footer>}
      </section>
      <section className="ui-card tickets-conversation" aria-label="Переписка по обращению">
        {detailLoading ? <div className="ui-loading">Загрузка переписки…</div> : !ticket ? <div className="tickets-placeholder"><span aria-hidden="true">✉</span><h2>Выберите обращение</h2><p>Откройте тикет слева, чтобы прочитать переписку и отправить сообщение.</p></div> : <>
          <header className="tickets-conversation__head"><div><span className="tickets-list__meta">Тикет #{ticket.id} · {dateLabel(ticket.created_at)}</span><h2>{ticket.subject}</h2><span className={`ticket-status ticket-status--${ticket.status}`}>{ticketStatusLabel[ticket.status]}</span></div>
            <button className="ui-btn ui-btn--outline ui-btn--sm" disabled={busy} onClick={() => void changeStatus()}>{ticket.status === 'closed' ? 'Возобновить' : 'Закрыть тикет'}</button></header>
          <div className="tickets-messages" aria-live="polite" aria-relevant="additions">
            {older && <button className="ui-btn ui-btn--outline ui-btn--sm tickets-messages__older" disabled={busy} onClick={() => void loadOlder()}>Более ранние сообщения</button>}
            {messages.map(message => <article key={message.id} className={`ticket-message ${message.is_staff ? 'ticket-message--staff' : ''}`}>
              <header><strong>{message.is_staff ? 'Поддержка' : message.author_name}</strong><time>{dateLabel(message.created_at)}</time></header><p>{message.body}</p>
            </article>)}
          </div>
          {ticket.status === 'closed' ? <div className="tickets-closed">Тикет закрыт. Вы можете возобновить его, если вопрос ещё не решён.</div> : <form className="tickets-reply" onSubmit={send}>
            <label htmlFor="ticket-reply">{isAdmin ? 'Ответ пользователю' : 'Ваше сообщение'}</label>
            <textarea id="ticket-reply" className="ui-input" placeholder="Напишите сообщение…" rows={4} maxLength={10000} value={draft} onChange={event => setDraft(event.target.value)} disabled={busy} required />
            <div><small>Не отправляйте пароли и коды подтверждения.</small><button className="ui-btn ui-btn--primary" disabled={busy || !draft.trim()}>{busy ? 'Отправка…' : 'Отправить сообщение'}</button></div>
          </form>}
        </>}
      </section>
    </div>
    {createOpen && <div className="area-modal" role="dialog" aria-modal="true" aria-labelledby="new-ticket-title"><div className="area-modal__backdrop" onClick={() => { if (!busy) setCreateOpen(false); }} />
      <div className="area-modal__dialog tickets-create" ref={dialogRef}><header className="area-modal__header"><h2 id="new-ticket-title" className="area-modal__title">Новое обращение</h2><button type="button" className="area-modal__close" aria-label="Закрыть" disabled={busy} onClick={() => setCreateOpen(false)}>×</button></header>
        <form onSubmit={create}><div className="tickets-create__body">
          {createError && <div className="ui-alert ui-alert--danger" role="alert">{createError}</div>}
          <label>Тема обращения<input className="ui-input" placeholder="Кратко опишите ваш вопрос" maxLength={200} value={subject} onChange={event => setSubject(event.target.value)} required disabled={busy} /></label>
          <label>Сообщение<textarea className="ui-input" placeholder="Что произошло? Что вы ожидали увидеть? Укажите название камеры или участка, если вопрос связан с ними." rows={7} maxLength={10000} value={body} onChange={event => setBody(event.target.value)} required disabled={busy} /></label>
          <p>Не указывайте пароли, коды из писем и реквизиты банковских карт.</p>
        </div><footer className="area-modal__footer"><button type="button" className="ui-btn ui-btn--outline" disabled={busy} onClick={() => setCreateOpen(false)}>Отмена</button><button className="ui-btn ui-btn--primary" disabled={busy || !subject.trim() || !body.trim()}>{busy ? 'Отправка…' : 'Создать тикет'}</button></footer></form>
      </div>
    </div>}
  </div></main>;
};
export default Tickets;
